import { useQuery } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useConfig } from 'hooks/use-config';

import { kbService } from '../agent-chat-module/services/kb.service';
import type {
  IAddQnAPayload,
  IAddFilesPayload,
  IAddLinkPayload,
  IAddTextPayload,
  ICreateFolderPayload,
  IDeleteFolderPayload,
  IDeleteKBPayload,
  IReembedKBPayload,
  IGetAgentsByFolderIdPayload,
  IGetAllKBPayload,
  IUpdateFolderPayload,
  IUpdateKBPayload,
  KBType,
} from '../agent-chat-module/types/kb.service.type';

export const useGetAllKB = (
  payload: IGetAllKBPayload,
  enabled = true,
  options?: {
    refetchInterval?:
      | number
      | false
      | ((query: any) => number | false | undefined);
    refetchIntervalInBackground?: boolean;
  }
) => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery({
    queryKey: ['kb', 'documents', payload],
    queryFn: () => kbService.getAllKB(payload, blocksKey),
    enabled: !!blocksKey && !!payload.project_key && enabled,
    refetchInterval: options?.refetchInterval,
    refetchIntervalInBackground: options?.refetchIntervalInBackground,
  });
};

export const useGetFolderList = (projectKey: string, enabled = true) => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery({
    queryKey: ['kb', 'folders', projectKey],
    queryFn: () => kbService.getFolderList(projectKey, blocksKey),
    enabled: !!blocksKey && !!projectKey && enabled,
  });
};

export const useCreateFolder = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['kb', 'create-folder'],
    mutationFn: (payload: ICreateFolderPayload) => kbService.createFolder(payload, blocksKey),
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({ queryKey: ['kb', 'folders', payload.project_key] });
    },
  });
};

export const useUpdateFolder = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['kb', 'update-folder'],
    mutationFn: (payload: IUpdateFolderPayload) => kbService.updateFolder(payload, blocksKey),
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({ queryKey: ['kb', 'folders', payload.project_key] });
    },
  });
};

export const useDeleteFolder = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['kb', 'delete-folder'],
    mutationFn: (payload: IDeleteFolderPayload) => kbService.deleteFolder(payload, blocksKey),
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({ queryKey: ['kb', 'folders', payload.project_key] });
    },
  });
};

export const useAddFiles = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['kb', 'add-files'],
    mutationFn: (payload: IAddFilesPayload) => kbService.addFiles(payload, blocksKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kb', 'documents'] });
    },
  });
};

export const useAddLink = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['kb', 'add-link'],
    mutationFn: (payload: IAddLinkPayload) => kbService.addLink(payload, blocksKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kb', 'documents'] });
    },
  });
};

export const useAddText = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['kb', 'add-text'],
    mutationFn: (payload: IAddTextPayload) => kbService.addText(payload, blocksKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kb', 'documents'] });
    },
  });
};

export const useAddQnA = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['kb', 'add-qna'],
    mutationFn: (payload: IAddQnAPayload) => kbService.addQnA(payload, blocksKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kb', 'documents'] });
    },
  });
};

export const useUpdateKB = (kbType: KBType) => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['kb', 'update', kbType],
    mutationFn: (payload: IUpdateKBPayload) => kbService.updateKb(payload, blocksKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kb', 'documents'] });
    },
  });
};

export const useDeleteKbById = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['kb', 'delete'],
    mutationFn: (payload: IDeleteKBPayload) => kbService.deleteKbById(payload, blocksKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kb', 'documents'] });
    },
  });
};

export const useReembedKb = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['kb', 'reembed'],
    mutationFn: (payload: IReembedKBPayload) => kbService.reembedKb(payload, blocksKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kb', 'documents'] });
    },
  });
};

export const useGetAgentsByFolderId = (
  payload: IGetAgentsByFolderIdPayload,
  enabled = true
) => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery({
    queryKey: ['kb', 'folder-agents', payload],
    queryFn: () => kbService.getAgentsByFolderId(payload, blocksKey),
    enabled: !!blocksKey && !!payload.project_key && !!payload.folder_id && enabled,
  });
};
